import { Link } from 'react-router-dom';
import { BellIcon, BellIcon as BellIconSolid } from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolidFilled } from '@heroicons/react/24/solid';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  iconSolid: React.ComponentType<{ className?: string }>;
}

interface MobileBottomNavProps {
  profile: {
    username: string;
    userId: string;
  };
  navigationItems: NavigationItem[];
  isActive: (path: string) => boolean;
  unreadCount: number;
  profileImageUrl: string;
  onImageError: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Mobile bottom navigation component.
 * Displays navigation tabs for mobile view with icons and labels.
 */
export const MobileBottomNav = ({
  profile,
  navigationItems,
  isActive,
  unreadCount,
  profileImageUrl,
  onImageError,
}: MobileBottomNavProps) => {
  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-surface border-t border-default shadow-lg">
      <nav className="flex justify-around items-center h-16 px-4">
        {navigationItems.map((item) => {
          const Icon = isActive(item.href) ? item.iconSolid : item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={classNames(
                isActive(item.href) ? 'text-brand' : 'text-muted hover:text-brand',
                'flex flex-col items-center justify-center flex-1 py-2 transition-colors duration-200',
              )}
            >
              <Icon className="h-6 w-6" />
              <span className="text-xs mt-1 font-medium">{item.name}</span>
            </Link>
          );
        })}

        {/* Notifications Button */}
        <Link
          to="/notifications"
          className={classNames(
            isActive('/notifications') ? 'text-brand' : 'text-muted hover:text-brand',
            'flex flex-col items-center justify-center flex-1 py-2 transition-colors duration-200',
          )}
        >
          <div className="relative">
            {isActive('/notifications') ? (
              <BellIconSolidFilled className="h-6 w-6" />
            ) : (
              <BellIcon className="h-6 w-6" />
            )}
            {unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center
                              justify-center rounded-full bg-primary px-1 text-[10px]
                              font-medium text-on-brand"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          <span className="text-xs mt-1 font-medium">Alerts</span>
        </Link>

        {/* Account Button with Profile Picture */}
        <Link
          to="/account"
          className="flex flex-col items-center justify-center flex-1 py-2 transition-colors duration-200"
        >
          <div
            className={classNames(
              isActive('/account') ? 'ring-2 ring-teal-600' : 'ring-2 ring-gray-300',
              'rounded-full transition-all duration-200',
            )}
          >
            <img
              className="w-6 h-6 rounded-full object-cover"
              alt={profile.username}
              src={profileImageUrl}
              onError={onImageError}
            />
          </div>
          <span
            className={classNames(
              isActive('/account') ? 'text-brand' : 'text-muted',
              'text-xs mt-1 font-medium',
            )}
          >
            Account
          </span>
        </Link>
      </nav>
    </div>
  );
};
